import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import ActivityChart from '../components/fitbit/ActivityChart';
import SleepDataView from '../components/fitbit/SleepDataView';
import HeartRateChart from '../components/fitbit/HeartRateChart';
import WeightChart from '../components/fitbit/WeightChart';
import SpO2Chart from '../components/fitbit/SpO2Chart';
import TemperatureChart from '../components/fitbit/TemperatureChart';
import NutritionDataView from '../components/data/NutritionDataView';
import HydrationDataView from '../components/data/HydrationDataView';
import SupplementsDataView from '../components/data/SupplementsDataView';
import FitnessDataView from '../components/data/FitnessDataView';
import MitoDataView from '../components/data/MitoDataView';
import LogHistory from '../components/data/LogHistory';

// Data: one tab per kind of number. Under the tab row, each view carries the
// shared time-range control (TimeRange.js) and then its charts. History leads
// because it's the record of what YOU logged, in your words; every other tab is
// a chart of one measurement. The tab lives in `?tab=` so Home can link straight
// to Sleep or Steps.

const TABS = [
  { key: 'history', label: 'History', View: LogHistory },
  { key: 'activity', label: 'Activity', fitbit: true, View: ActivityChart },
  { key: 'workouts', label: 'Workouts', View: FitnessDataView },
  { key: 'sleep', label: 'Sleep', fitbit: true, View: SleepDataView },
  { key: 'heart-rate', label: 'Heart', fitbit: true, View: HeartRateChart },
  { key: 'weight', label: 'Weight', View: WeightChart },
  { key: 'spo2', label: 'Blood oxygen', fitbit: true, View: SpO2Chart },
  { key: 'temperature', label: 'Skin temperature', fitbit: true, View: TemperatureChart },
  { key: 'mito', label: 'Energy index', View: MitoDataView },
  { key: 'nutrition', label: 'Food', View: NutritionDataView },
  { key: 'supplements', label: 'Supplements', View: SupplementsDataView },
  { key: 'hydration', label: 'Water', View: HydrationDataView },
];

const tabClass = (on) =>
  `shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
    on ? 'border-fg bg-fg text-canvas' : 'border-line-strong text-fg-2 hover:text-fg'
  }`;

const DataPage = () => {
  const { fitbitStatus, loading: loadingStatus } = useFitbitStatus();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const active = TABS.some((t) => t.key === requested) ? requested : 'history';
  const tab = TABS.find((t) => t.key === active);

  const pick = (key) => setParams(key === 'history' ? {} : { tab: key });

  // Keep the active tab in view in the horizontal scroller on phones (scrollLeft
  // only, so the page never nudges vertically).
  useEffect(() => {
    document.querySelectorAll('.data-tabs').forEach((c) => {
      const chip = c.querySelector(`[data-tab="${active}"]`);
      if (chip) c.scrollLeft = chip.offsetLeft - c.clientWidth / 2 + chip.clientWidth / 2;
    });
  }, [active]);

  const needsFitbit = tab.fitbit && !loadingStatus && !fitbitStatus.connected;

  return (
    <div className="mx-auto max-w-[1080px] px-4 sm:px-6 lg:px-10">
      <style>{`.data-tabs{scrollbar-width:none;-ms-overflow-style:none}
        .data-tabs::-webkit-scrollbar{display:none}`}</style>

      <div className="mb-[22px]">
        <h1 className="text-[1.75rem] font-medium leading-[1.15] tracking-[-0.02em]">Data</h1>
        <p className="mt-1 max-w-[40em] text-fg-3">Everything we have on you, one tab per number.</p>
      </div>

      <div className="data-tabs -mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Data tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" data-tab={t.key}
            aria-selected={active === t.key} onClick={() => pick(t.key)} className={tabClass(active === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {needsFitbit ? (
          <div className="rounded-xl border border-line px-4 py-3 text-fg-2">
            This one comes from a watch. <Link to="/me/devices" className="font-medium text-fg hover:underline">Connect a device →</Link>
          </div>
        ) : loadingStatus && tab.fitbit ? (
          <div className="h-40 animate-pulse rounded-xl border border-line" />
        ) : (
          <tab.View key={active} />
        )}
      </div>
    </div>
  );
};

export default DataPage;
